/* PGEnglishProficiency.js
   PG-specific English Proficiency Assessment component (Step 8).
   Logic:
   1. If covAlumni === 'Yes' → show exemption notice, no test input
   2. If taughtInEnglish === 'Yes' → no test input
   3. If taughtInEnglish === 'No' → show test section — mirrors UG TestScores.js:
      - Test checkbox (IELTS / TOEFL / PTE / DET)
      - Test Type dropdown populated from API: GetTestTypeByTest?TestId=
      - Score + Date Taken
*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import InputRow from './InputRow'
import Loading from './Loading'

const RADIO_OPTIONS = [
  { Id: 'Yes', value: 'Yes' },
  { Id: 'No', value: 'No' }
]

/* Test IDs matching the UG TestScores.js / backend AcademicTestController */
const CHECKBOX_TESTS = [
  { id: 8,  label: 'IELTS' },
  { id: 9,  label: 'TOEFL' },
  { id: 14, label: 'PTE' },
  { id: 15, label: 'DET (Duolingo)' }
]

const EMPTY_ROW = { test: '', testType: '', score: '', dateTaken: '' }

export default class PGEnglishProficiency extends Component {
  constructor (props) {
    super(props)
    const tests = props.tests && props.tests.length > 0 ? props.tests : [{ ...EMPTY_ROW }]
    /* Determine which checkbox was ticked from saved tests */
    const checkboxTest = tests.length > 0 && tests[0].test ? tests[0].test : 0

    this.state = {
      taughtInEnglish: props.taughtInEnglish || '',
      tests: tests,
      checkboxTest: checkboxTest,
      testTypeData: [],   // per-row array of JSON strings (like UG)
      loading: false,
      scoreError: ''
    }
    this.publish = this.publish.bind(this)
    this.handleCheckbox = this.handleCheckbox.bind(this)
    this.getTestTypeData = this.getTestTypeData.bind(this)
    this.updateTestType = this.updateTestType.bind(this)
    this.updateScore = this.updateScore.bind(this)
    this.updateDateTaken = this.updateDateTaken.bind(this)
  }

  componentDidMount () {
    /* Publish initial state so Main.js is in sync even if user never interacts */
    this.publish()

    /* If arriving with pre-filled tests, load their test types from the API */
    const { tests } = this.state
    if (tests && tests.length > 0 && tests[0].test) {
      tests.forEach((t, i) => {
        if (t.test) this.getTestTypeData(t.test, i)
      })
    }
  }

  async getTestTypeData (testId, index) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/AcademicTest/GetTestTypeByTest?TestId=${testId}`)
      .then(res => res.json())
      .then(res => {
        const testTypeData = Array.from(this.state.testTypeData)
        testTypeData[index] = JSON.stringify(res || [])
        this.setState({ testTypeData })
        return 1
      })
      .catch(() => {})
  }

  publish (override = {}) {
    const s = { ...this.state, ...override }
    this.props.collectState('PG_EP', {
      taughtInEnglish: s.taughtInEnglish,
      tests: s.taughtInEnglish === 'No' ? s.tests : []
    })
  }

  /* Mirrors UG addTest — populates rows using the selected test ID */
  async handleCheckbox (testId) {
    const { checkboxTest } = this.state

    if (checkboxTest === testId) {
      /* Unchecking — clear */
      const empty = [{ ...EMPTY_ROW }]
      this.setState({ checkboxTest: 0, tests: empty, testTypeData: [] }, () => this.publish())
      return
    }

    /* Switching to a different test — build multiple rows using GetTestTypeByTest
       then auto-fill testType per row (same pattern as UG TestScores.js addTest) */
    this.setState({ checkboxTest: testId, tests: [{ ...EMPTY_ROW }], testTypeData: [] })

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AcademicTest/GetTestTypeByTest?TestId=${testId}`)
      const types = await res.json()

      if (!types || types.length === 0) {
        /* No sub-types → single row */
        const rows = [{ test: testId, testType: '', score: '', dateTaken: '' }]
        const testTypeData = [JSON.stringify([])]
        this.setState({ tests: rows, testTypeData }, () => this.publish())
        return
      }

      /* Build one row per test type, pre-filling test + testType */
      const rows = types.map(tt => ({ test: testId, testType: tt.Id, score: '', dateTaken: '' }))
      const testTypeData = types.map(() => JSON.stringify(types))
      this.setState({ tests: rows, testTypeData }, () => this.publish())
    } catch (e) {
      const rows = [{ test: testId, testType: '', score: '', dateTaken: '' }]
      this.setState({ tests: rows, testTypeData: [JSON.stringify([])] }, () => this.publish())
    }
  }

  updateTestType (index, val) {
    const tests = this.state.tests.map((t, i) => i === index ? { ...t, testType: val } : t)
    this.setState({ tests }, () => this.publish())
  }

  updateScore (index, val) {
    const tests = this.state.tests.map((t, i) => i === index ? { ...t, score: val } : t)
    this.setState({ tests }, () => this.publish())
  }

  updateDateTaken (index, val) {
    const tests = this.state.tests.map((t, i) => i === index ? { ...t, dateTaken: val } : t)
    this.setState({ tests }, () => this.publish())
  }

  render () {
    const { loading, taughtInEnglish, tests, testTypeData, checkboxTest } = this.state
    const { error, covAlumni } = this.props

    if (loading) return <Loading />

    /* Coventry graduates are exempt from English proficiency requirements */
    if (covAlumni === true || covAlumni === 'Yes') {
      return (
        <div>
          <div style={{
            background: '#e8f4fd',
            border: '1px solid #b3d9f0',
            borderRadius: 8,
            padding: '16px 20px',
            marginBottom: 16
          }}>
            <p style={{ margin: 0, color: '#2874a6', fontWeight: 'bold' }}>
              ✓ English Proficiency Exemption
            </p>
            <p style={{ margin: '6px 0 0', color: '#5d6d7e', fontSize: 14 }}>
              As a Coventry University graduate, you are exempt from the English language proficiency requirement.
              No test scores are needed.
            </p>
          </div>
          <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
        </div>
      )
    }

    return (
      <div>
        {/* Q1: Was your bachelor's degree taught in English? */}
        <p style={{ fontWeight: 'bold', marginBottom: 8 }}>
          Was your previous bachelor&apos;s degree taught in English? <span style={{ color: 'red' }}>*</span>
        </p>
        <div style={{ marginBottom: 16 }}>
          {RADIO_OPTIONS.map(opt => (
            <label key={opt.Id} style={{ marginRight: 24, cursor: 'pointer' }}>
              <input
                type='radio'
                name='taughtInEnglish'
                value={opt.Id}
                checked={taughtInEnglish === opt.Id}
                onChange={() => {
                  this.setState({ taughtInEnglish: opt.Id }, () => this.publish())
                }}
                style={{ minWidth: 'auto', marginRight: 6 }}
              />
              {opt.value}
            </label>
          ))}
          {error.taughtInEnglish && (
            <p style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{error.taughtInEnglish}</p>
          )}
        </div>

        {/* Test score section — only if degree was NOT taught in English */}
        {taughtInEnglish === 'No' && (
          <div style={{ paddingLeft: 12, borderLeft: '3px solid #6AA8FF' }}>
            <p style={{ fontWeight: 'bold', marginBottom: 8, color: '#3168CE' }}>
              English Language Proficiency Test Scores
            </p>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
              If you have taken any of the tests below, please fill in all of the required boxes.
            </p>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
              If your results are not out yet, please do not enter data in this section.
            </p>

            {/* Test checkboxes — same as UG TestScores.js */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
              {CHECKBOX_TESTS.map(ct => (
                <label key={ct.id} style={{ cursor: 'pointer' }}>
                  <input
                    type='checkbox'
                    name='engTest'
                    checked={checkboxTest === ct.id}
                    onChange={() => this.handleCheckbox(ct.id)}
                    style={{ minWidth: 'auto', marginRight: 6 }}
                  />
                  {ct.label}
                </label>
              ))}
            </div>

            {/* Test rows — Test + Type + Score + Date (same layout as UG) */}
            {[8, 9, 14, 15].includes(checkboxTest) && (
              <div>
                {tests.map((t, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: 12,
                      marginBottom: 10,
                      flexWrap: 'wrap'
                    }}
                  >
                    {/* Test name — read-only, auto-filled by checkbox */}
                    <div style={{ minWidth: 140 }}>
                      <InputRow
                        rowName='Test'
                        name='select'
                        value={t.test}
                        data={CHECKBOX_TESTS.map(c => ({ Id: c.id, value: c.label }))}
                        disabled='true'
                        onChange={() => {}}
                        errMsg=''
                      />
                    </div>

                    {/* Test Type — fetched from API per test */}
                    <div style={{ minWidth: 160 }}>
                      <InputRow
                        rowName='Type'
                        name='select'
                        value={t.testType}
                        data={t.test && testTypeData[index] ? JSON.parse(testTypeData[index] || '[]') : []}
                        disabled='true'
                        errMsg={error.tests && error.tests[index] ? error.tests[index].testType || '' : ''}
                        onChange={(val) => this.updateTestType(index, val)}
                      />
                    </div>

                    {/* Score */}
                    <div style={{ minWidth: 100 }}>
                      <InputRow
                        rowName='Score'
                        name='text'
                        value={t.score}
                        required='*'
                        errMsg={error.tests && error.tests[index] ? error.tests[index].score || '' : ''}
                        onChange={(val) => {
                          if (val.length <= 6 || val === '') {
                            this.updateScore(index, val)
                            this.setState({ scoreError: '' })
                          } else {
                            this.setState({ scoreError: 'Maximum 6 characters allowed.' })
                          }
                        }}
                      />
                      {this.state.scoreError && <p style={{ color: 'red', fontSize: 12 }}>{this.state.scoreError}</p>}
                    </div>

                    {/* Date Taken */}
                    <div style={{ minWidth: 160 }}>
                      <InputRow
                        rowName='Date Taken'
                        name='date'
                        value={t.dateTaken}
                        required='*'
                        errMsg={error.tests && error.tests[index] ? error.tests[index].dateTaken || '' : ''}
                        onChange={(val) => {
                          const d = new Date(val)
                          if (!isNaN(d.getFullYear()) && parseInt(d.getFullYear()) <= 9999) {
                            this.updateDateTaken(index, val)
                          } else {
                            this.updateDateTaken(index, '')
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
      </div>
    )
  }
}

PGEnglishProficiency.propTypes = {
  onSave: PropTypes.func.isRequired,
  collectState: PropTypes.func.isRequired,
  error: PropTypes.object,
  covAlumni: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  taughtInEnglish: PropTypes.string,
  tests: PropTypes.array
}

PGEnglishProficiency.defaultProps = {
  error: {},
  covAlumni: false,
  taughtInEnglish: '',
  tests: []
}
